import {ChangeDetectionStrategy, Component, effect, ElementRef, inject, viewChild} from '@angular/core';
import {DeButtonComponent, DeCheckboxComponent, DeModalComponent} from '@docestate/ui-angular';
import {TranslocoDirective} from '@jsverse/transloco';
import {ConsentService} from '../../services/consent.service';

@Component({
  selector: 'app-consent-modal',
  imports: [DeButtonComponent, DeCheckboxComponent, DeModalComponent, TranslocoDirective],
  templateUrl: './consent-modal.html',
  styleUrl: './consent-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:focusin)': 'onDocumentFocus($event)',
  },
})
export class ConsentModal {
  private readonly consentService = inject(ConsentService);
  private readonly primaryButton = viewChild('primaryButton', { read: ElementRef });
  private readonly modalRoot = viewChild('modalRoot', { read: ElementRef });

  protected readonly isOpen = this.consentService.isModalOpen;
  protected readonly view = this.consentService.currentView;
  protected readonly analyticsSelected = this.consentService.analyticsSelected;

  /**
   * Initializes a new instance of the class. Sets up a reactive effect that focuses
   * the primary button when the component is open and the button is available.
   */
  constructor() {
    effect(() => {
      if (!this.isOpen()) {
        return;
      }

      const button = this.primaryButton();
      if (!button) {
        return;
      }

      setTimeout(() => this.focusPrimaryButton(button), 0);
    });
  }

  /**
   * Handles the action of accepting all consent preferences.
   * This method invokes the consent service to mark all consent options as accepted.
   *
   * @return {void} No return value.
   */
  onAcceptAll(): void {
    this.consentService.acceptAll();
  }

  /**
   * Handles the acceptance of necessary consents by invoking the relevant service method.
   * This method is typically called when a user agrees to only the essential consents.
   *
   * @return {void} Does not return a value.
   */
  onAcceptNecessary(): void {
    this.consentService.acceptNecessary();
  }

  /**
   * Opens the settings menu using the associated consent service.
   *
   * @return {void} This method does not return a value.
   */
  onOpenSettings(): void {
    this.consentService.openSettings();
  }

  /**
   * Navigates back to the introductory screen by triggering the `backToIntro` method
   * of the `consentService`.
   *
   * @return {void} No return value.
   */
  onBackToIntro(): void {
    this.consentService.backToIntro();
  }

  /**
   * Handles the process of saving user settings by invoking the save action
   * on the consent service. This method commits updated settings as provided.
   *
   * @return {void} No return value.
   */
  onSaveSettings(): void {
    this.consentService.saveSettings();
  }

  /**
   * Toggles the analytics consent based on the DocEstate checkbox value.
   *
   * @param {boolean} isSelected - Whether analytics consent is currently selected.
   * @return {void} Does not return a value.
   */
  onAnalyticsToggle(isSelected: boolean): void {
    this.consentService.setAnalyticsSelection(isSelected);
  }

  /**
   * Handles the focus event on the document. Ensures that focus is redirected to a specific element
   * if a condition regarding the modal and event target is met.
   *
   * @param {FocusEvent} event - The focus event triggered on the document.
   * @return {void} This method does not return any value.
   */
  onDocumentFocus(event: FocusEvent): void {
    if (!this.isOpen()) {
      return;
    }

    const modal = this.modalRoot();
    const target = event.target as HTMLElement | null;
    if (!modal || !target || modal.nativeElement.contains(target)) {
      return;
    }

    const button = this.primaryButton();
    if (button) {
      this.focusPrimaryButton(button);
    }
  }

  private focusPrimaryButton(buttonHost: ElementRef): void {
    const nativeElement = buttonHost.nativeElement as HTMLElement;
    const button = nativeElement.matches('button')
      ? nativeElement
      : nativeElement.querySelector<HTMLButtonElement>('button');

    button?.focus();
  }
}
